import React from "react";

import { Button, Input, Modal, Select } from "../../ui";
import {
  clampSequenceDelay,
  getSequenceDelayValidationError,
  SEQUENCE_DELAY_LIMITS,
  SEQUENCE_DELAY_UNIT_OPTIONS,
  type SequenceDelayUnit,
} from "../../../utils/sequenceDelay";

type DelayBeforeActionModalProps = {
  isOpen: boolean;
  onClose: () => void;
  initialValue: number;
  initialUnit?: SequenceDelayUnit;
  onSave: (nextValue: number, unit: SequenceDelayUnit) => void;
};

export default function DelayBeforeActionModal({
  isOpen,
  onClose,
  initialValue,
  initialUnit = "days",
  onSave,
}: DelayBeforeActionModalProps) {
  const [value, setValue] = React.useState(String(initialValue));
  const [unit, setUnit] = React.useState<SequenceDelayUnit>(initialUnit);
  const validationError = React.useMemo(
    () => getSequenceDelayValidationError(value, unit),
    [value, unit]
  );
  const isValid = validationError.length === 0;
  const limits = SEQUENCE_DELAY_LIMITS[unit];

  React.useEffect(() => {
    if (!isOpen) return;
    setValue(String(initialValue));
    setUnit(initialUnit);
  }, [initialUnit, initialValue, isOpen]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="small"
      title="Delay before the next action"
      closeIconSrc="/icons/x_circle.svg"
      panelClassName="bg-white p-4"
      headerClassName="rounded-t-[12px] border border-border-grey border-b-0 bg-white px-4 pt-3"
      titleClassName="text-xl !font-medium !text-center"
      bodyClassName="rounded-b-[12px] border border-t-0 border-border-grey bg-white px-4 py-4"
    >
      <div className="space-y-4">
        <div className="grid grid-cols-[1fr_220px] gap-3">
          <Input
            type="number"
            min={limits.min}
            max={limits.max}
            step={1}
            value={value}
            size="sm"
            onChange={(event) => setValue(event.target.value)}
          />
          <Select
            options={SEQUENCE_DELAY_UNIT_OPTIONS.map((option) => ({
              value: option.value,
              label: option.label,
            }))}
            value={unit}
            onChange={(next) =>
              setUnit(next === "hours" || next === "days" ? next : "days")
            }
            size="sm"
          />
        </div>
        {validationError ? <p className="text-xs text-red-600">{validationError}</p> : null}

        <Button
          variant="primary"
          size="sm"
          fullWidth
          disabled={!isValid}
          onClick={() => {
            if (!isValid) return;
            onSave(clampSequenceDelay(Number(value), unit), unit);
          }}
        >
          Save
        </Button>
      </div>
    </Modal>
  );
}
